import type { ButtonHTMLAttributes, ReactNode } from 'react'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  readonly fullWidth?: boolean
  /** Shows a spinner and blocks activation. Prefer this over disabling by
   *  hand: it keeps the accessible name and announces busy state. */
  readonly loading?: boolean
  readonly children: ReactNode
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: styles.primary ?? '',
  secondary: styles.secondary ?? '',
  ghost: styles.ghost ?? '',
  danger: styles.danger ?? '',
}
const SIZES: Record<ButtonSize, string> = { sm: styles.sm ?? '', md: '', lg: styles.lg ?? '' }

/**
 * `type` defaults to "button". HTML's own default is "submit", which inside
 * a form makes every unannotated button submit it — the kind of bug that
 * only shows up once a form has two buttons.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled = false,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const className = [styles.button, VARIANTS[variant], SIZES[size], fullWidth ? styles.full : '']
    .filter(Boolean)
    .join(' ')
  return (
    <button
      {...rest}
      type={type}
      className={className}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {children}
    </button>
  )
}
